import type { PropsWithChildren } from 'react';
import { useMemo } from 'react';
import { noop } from 'lodash';
import { createContext, useContextSelector } from 'use-context-selector';
import type { ValidHref } from '@frameio/shared/routing/types';
import type { FolderDndNamespace } from '../components';
import type { ProjectTreeItem } from '../types';

export type ProjectTreeContextValue = {
  /** The base depth of the tree */
  baseDepth?: number;
  /** Disable the context menus */
  disableContextMenus?: boolean;
  /** Disable the mount project adornment on the root folder */
  disableOpenOnDesktop?: boolean;
  /** Enables hidden links for each item. */
  enableHiddenLinks?: boolean;
  /** Namespace used for folder dnd */
  folderDndNamepace: FolderDndNamespace | null;
  /** Callback fired when a folder is selected by click or enter key. */
  onItemSelect: (item: ProjectTreeItem, href?: ValidHref) => void;
  /** Text to override the All Assets with */
  overrideRootFolderName?: string;
  /** The id of a "selected" item, which overrides the global active item. */
  selectedItem?: ProjectTreeItem;
  /** Callback used to toggle an item's expanded/collapsed state. */
  toggleItem?: (item: ProjectTreeItem, opened: boolean) => void;
};

type Props = PropsWithChildren<Omit<ProjectTreeContextValue, 'activeFolderId'>>;

const ProjectTreeContext = createContext<ProjectTreeContextValue>({
  baseDepth: 0,
  disableContextMenus: false,
  disableOpenOnDesktop: false,
  enableHiddenLinks: false,
  folderDndNamepace: null,
  onItemSelect: noop,
  overrideRootFolderName: undefined,
  selectedItem: undefined,
  toggleItem: undefined,
});

/**
 * The ProjectTreeContext provides many different settings to the underlying components
 * within a ProjectTree.
 */
export function ProjectTreeContextProvider({
  baseDepth = 0,
  children,
  disableContextMenus = false,
  disableOpenOnDesktop = false,
  enableHiddenLinks = false,
  folderDndNamepace,
  onItemSelect,
  overrideRootFolderName,
  selectedItem,
  toggleItem,
}: Props) {
  const value = useMemo<ProjectTreeContextValue>(
    () => ({
      baseDepth,
      disableContextMenus,
      disableOpenOnDesktop,
      enableHiddenLinks,
      folderDndNamepace,
      onItemSelect,
      overrideRootFolderName,
      selectedItem,
      toggleItem,
    }),
    [
      baseDepth,
      disableContextMenus,
      disableOpenOnDesktop,
      enableHiddenLinks,
      folderDndNamepace,
      onItemSelect,
      overrideRootFolderName,
      selectedItem,
      toggleItem,
    ]
  );

  return (
    <ProjectTreeContext.Provider value={value}>
      <>{children}</>
    </ProjectTreeContext.Provider>
  );
}

export const useBaseDepth = () =>
  useContextSelector(ProjectTreeContext, ({ baseDepth }) => baseDepth ?? 0);

export const useFolderDndNamespace = () =>
  useContextSelector(
    ProjectTreeContext,
    ({ folderDndNamepace }) => folderDndNamepace
  );

export const useIsActiveItem = (item: ProjectTreeItem) =>
  useContextSelector(
    ProjectTreeContext,
    ({ selectedItem }) =>
      selectedItem?.id === item.id && selectedItem.type === item.type
  );

export const useIsContextMenuDisabled = () =>
  useContextSelector(
    ProjectTreeContext,
    ({ disableContextMenus }) => disableContextMenus
  );

export const useIsOpenOnDesktopDisabled = () =>
  useContextSelector(
    ProjectTreeContext,
    ({ disableOpenOnDesktop }) => disableOpenOnDesktop ?? false
  );

export const useOverrideRootFolderName = () =>
  useContextSelector(
    ProjectTreeContext,
    ({ overrideRootFolderName }) => overrideRootFolderName
  );

export const useToggleItem = () =>
  useContextSelector(ProjectTreeContext, ({ toggleItem }) => toggleItem);

export const useOnItemSelect = () =>
  useContextSelector(ProjectTreeContext, ({ onItemSelect }) => onItemSelect);

export const useShouldRenderLinks = () =>
  useContextSelector(
    ProjectTreeContext,
    ({ enableHiddenLinks }) => enableHiddenLinks ?? false
  );
